// src/components/corkboard/ManuscriptPreview.jsx
//
// The small book at the top of the right rail: the manuscript's chapters as a
// table of contents, with a spine that grows as the book does.
//
// It answers the question the board otherwise cannot — "where is any of this
// actually written?" — without leaving the board to find out. The elements on
// the cork are planning; this is the thing being planned.
//
// **The chapters are real, and are not the scenes.** In the prototype a scene
// *was* a chapter, so its preview listed scene cards. Here a chapter is a
// heading in the manuscript, derived from the document by outline.js, so this
// lists what the book actually contains. A board with ten scenes and an empty
// manuscript correctly shows an empty book, which is information.
//
// **Rows do not jump.** The editor has no deep link to a block today, so a row
// that looked clickable would open the manuscript at the top and pretend it had
// gone somewhere. One honest "Open" link instead; a per-chapter jump is worth
// having, but it is editor work and belongs with whoever adds it there.
import React from 'react';
import { Box, Typography } from '@mui/material';

import { CB, FONT, eyebrowSx } from './corkTokens';

// The spine grows with the book, between a pamphlet and something that fits the
// widget. A spine that scaled without a ceiling would push the board's details
// panel off the screen for a long manuscript.
const SPINE_MIN = 28;
const SPINE_MAX = 120;
const SPINE_PER_CHAPTER = 11;

export default function ManuscriptPreview({ chapters, onOpen }) {
  const spineHeight = Math.max(
    SPINE_MIN,
    Math.min(SPINE_MAX, SPINE_MIN + chapters.length * SPINE_PER_CHAPTER),
  );

  return (
    <Box sx={{
      backgroundColor: CB.lavender,
      borderBottom: `1px solid ${CB.lineSoft}`,
      px: 2.5, pt: 2.25, pb: 2.5,
    }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5,
      }}>
        <Typography component="span" sx={eyebrowSx}>Manuscript</Typography>
        <Box
          component="button"
          type="button"
          onClick={onOpen}
          sx={{
            fontFamily: FONT.mono, fontSize: 10, color: CB.purple,
            background: 'none', border: 'none', p: 0, cursor: 'pointer',
            textDecoration: 'underline',
            '&:hover': { color: CB.purpleMid },
          }}
        >
          Open →
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.25 }}>
        <Box sx={{
          width: 26, height: spineHeight, flexShrink: 0,
          borderRadius: '2px 0 0 2px',
          background: `linear-gradient(180deg, ${CB.purple} 0%, ${CB.purpleDeep} 100%)`,
          boxShadow: '0 6px 14px -6px rgba(35,31,77,0.4)',
          transition: 'height .3s ease',
        }} />

        <Box sx={{
          flex: 1, minWidth: 0,
          backgroundColor: CB.white,
          border: `1px solid ${CB.lineSoft}`,
          borderRadius: '0 3px 3px 0',
          px: 1.5, py: 1.25,
          maxHeight: 150, overflowY: 'auto',
        }}>
          {chapters.length === 0 ? (
            <Typography sx={{ fontFamily: FONT.mono, fontSize: 10.5, color: CB.gray }}>
              No chapters yet — add a heading in the editor and it appears here.
            </Typography>
          ) : chapters.map((chapter, i) => (
            <Box key={chapter.blockId} sx={{ display: 'flex', gap: 0.75, py: 0.4 }}>
              <Typography component="span" sx={{
                fontFamily: FONT.mono, fontSize: 11, color: CB.goldDeep, flexShrink: 0,
              }}>
                {i + 1}.
              </Typography>
              <Typography component="span" sx={{
                fontFamily: FONT.sans, fontSize: 11, color: CB.black,
                // Nesting is flattened on purpose: this is a count of what is in
                // the book, not the outline tree the editor already shows.
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {chapter.text || 'Untitled chapter'}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Typography sx={{ fontFamily: FONT.mono, fontSize: 10, color: CB.gray, mt: 1.25 }}>
        {chapters.length} chapter{chapters.length === 1 ? '' : 's'} · grows as you write
      </Typography>
    </Box>
  );
}
